/*
  Collection v2 · filtering without page reloads.

  Any link marked [data-col2-link] (chips, finder rows, active pills, clear,
  pagination) and every change in the Filter & sort drawer or the sort select
  re-renders only the grid section through Shopify's Section Rendering API
  (?section_id=…), then updates the address bar so the URL stays shareable
  and indexable. Back/forward re-render too. Without JS everything still works
  as normal links and a GET form.

  Card add-to-cart is handled by lp-v2.js (window.QOTTlp); after each
  re-render we call QOTTlp.bindAll() so new cards are wired.
*/
(() => {
    if (window.QOTTcol) return;

    const grid = () => document.querySelector('[data-col2]');
    const sectionId = () => { const g = grid(); return g && g.dataset.sectionId; };

    let controller = null;

    const render = async (url, { push = true, keepDrawer = false, scroll = false } = {}) => {
        const id = sectionId();
        if (!id) { window.location.href = url; return; }
        const target = new URL(url, window.location.origin);
        const fetchUrl = new URL(target);
        fetchUrl.searchParams.set('section_id', id);
        fetchUrl.hash = '';

        if (controller) controller.abort();
        controller = new AbortController();
        const current = grid();
        current.classList.add('is-loading');
        try {
            const res = await fetch(fetchUrl.toString(), { signal: controller.signal, credentials: 'same-origin' });
            if (!res.ok) throw new Error('HTTP ' + res.status);
            const html = await res.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const fresh = doc.querySelector('[data-col2]');
            if (!fresh) throw new Error('No grid in response');
            const wasOpen = keepDrawer && isOpen();
            current.replaceWith(fresh);
            if (wasOpen) openDrawer(false);
            if (push) {
                target.searchParams.delete('section_id');
                history.pushState({ col2: true }, '', target.pathname + target.search + (target.hash || ''));
            }
            if (window.QOTTlp && window.QOTTlp.bindAll) window.QOTTlp.bindAll();
            if (scroll) {
                const anchor = fresh.querySelector('.col2-anchor');
                if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            document.dispatchEvent(new CustomEvent('col2:updated', { detail: { url: target.toString() } }));
        } catch (err) {
            if (err.name === 'AbortError') return;
            console.warn('QOTTcol: falling back to a full page load', err);
            window.location.href = url;
        } finally {
            const g = grid();
            if (g) g.classList.remove('is-loading');
        }
    };

    // ---------- drawer ----------
    const drawer = () => document.querySelector('[data-col2-drawer]');
    const isOpen = () => { const d = drawer(); return d && !d.hidden; };
    let lastFocus = null;
    const openDrawer = (focus = true) => {
        const d = drawer(); if (!d) return;
        lastFocus = document.activeElement;
        d.hidden = false;
        requestAnimationFrame(() => d.classList.add('is-open'));
        document.documentElement.classList.add('col2-lock');
        const btn = document.querySelector('[data-col2-open]');
        if (btn) btn.setAttribute('aria-expanded', 'true');
        if (focus) { const x = d.querySelector('.col2-drawer__x'); if (x) x.focus(); }
    };
    const closeDrawer = () => {
        const d = drawer(); if (!d) return;
        d.classList.remove('is-open');
        document.documentElement.classList.remove('col2-lock');
        const btn = document.querySelector('[data-col2-open]');
        if (btn) btn.setAttribute('aria-expanded', 'false');
        setTimeout(() => { d.hidden = true; }, 250);
        if (lastFocus && lastFocus.focus) lastFocus.focus();
    };

    const formUrl = (form) => {
        const params = new URLSearchParams();
        new FormData(form).forEach((value, key) => {
            if (String(value).trim() === '') return;
            params.append(key, value);
        });
        const q = params.toString();
        return form.getAttribute('action') + (q ? '?' + q : '');
    };

    let debounce = null;

    document.addEventListener('click', (e) => {
        const link = e.target.closest('a[data-col2-link]');
        if (link && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
            e.preventDefault();
            const inDrawer = !!link.closest('[data-col2-drawer]');
            const fromStory = !link.closest('[data-col2]');
            if (inDrawer) closeDrawer();
            render(link.href, { scroll: fromStory || !!link.closest('.col2-pages') });
            return;
        }
        // Finder chips: jump to the product's card in the grid (CA visitors have
        // the -ca card); open the product page if the card is not on this page.
        const chip = e.target.closest('a[data-col2-card]');
        if (chip && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
            const h = chip.dataset.col2Card;
            const card = document.getElementById('col2-' + h) || document.getElementById('col2-' + h + '-ca') || document.getElementById('col2-' + h.replace(/-ca$/, ''));
            if (card) {
                e.preventDefault();
                card.scrollIntoView({ behavior: 'smooth', block: 'center' });
                card.classList.remove('is-flash');
                void card.offsetWidth;
                card.classList.add('is-flash');
            }
            return;
        }
        if (e.target.closest('[data-col2-open]')) { e.preventDefault(); openDrawer(); return; }
        if (e.target.closest('[data-col2-close]')) { e.preventDefault(); closeDrawer(); return; }
        const jump = e.target.closest('a[data-col2-jump]');
        if (jump) {
            const anchor = document.querySelector('.col2-anchor');
            if (anchor) { e.preventDefault(); anchor.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
        }
    });

    // Drawer: live-update the grid as options change, close on "Show results".
    document.addEventListener('change', (e) => {
        const form = e.target.closest('[data-col2-form]');
        if (form) {
            clearTimeout(debounce);
            debounce = setTimeout(() => render(formUrl(form), { keepDrawer: true }), e.target.type === 'number' ? 600 : 150);
            return;
        }
        const sort = e.target.closest('[data-col2-sort]');
        if (sort) {
            const url = new URL(window.location.href);
            url.searchParams.set('sort_by', sort.value);
            url.searchParams.delete('page');
            render(url.toString());
        }
    });

    document.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-col2-form]');
        if (!form) return;
        e.preventDefault();
        clearTimeout(debounce);
        closeDrawer();
        render(formUrl(form), { scroll: true });
    });

    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && isOpen()) closeDrawer(); });

    window.addEventListener('popstate', () => {
        if (grid()) render(window.location.href, { push: false });
    });

    window.QOTTcol = { render, openDrawer, closeDrawer };
})();
